import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    public?: boolean;
    requiresPermission?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/pages/LoginPage.vue"),
    meta: { public: true },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import("@/pages/ForgotPasswordPage.vue"),
    meta: { public: true },
  },
  {
    path: "/reset-password/:token",
    name: "reset-password",
    component: () => import("@/pages/ResetPasswordPage.vue"),
    meta: { public: true },
    props: true,
  },
  {
    path: "/accept-invite/:token",
    name: "accept-invite",
    component: () => import("@/pages/AcceptInvitePage.vue"),
    meta: { public: true },
    props: true,
  },
  {
    path: "/2fa-challenge",
    name: "2fa-challenge",
    component: () => import("@/pages/TwoFactorChallengePage.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/AppLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: { name: "dashboard" } },
      { path: "dashboard", name: "dashboard", component: () => import("@/pages/DashboardPage.vue") },
      { path: "me", name: "me", component: () => import("@/pages/MePage.vue") },
      {
        path: "me/sessions",
        name: "me-sessions",
        component: () => import("@/pages/MeSessionsPage.vue"),
      },
      {
        path: "admin/organizations",
        name: "admin-organizations",
        component: () => import("@/pages/AdminOrganizationsPage.vue"),
        meta: { requiresPermission: "org:manage_all" },
      },
      {
        path: "settings/organization",
        name: "settings-organization",
        component: () => import("@/pages/SettingsOrganizationPage.vue"),
        meta: { requiresPermission: "tenant:admin" },
      },
      {
        path: "settings/users",
        name: "settings-users",
        component: () => import("@/pages/SettingsUsersPage.vue"),
        meta: { requiresPermission: "user:read" },
      },
      {
        path: "settings/invitations",
        name: "settings-invitations",
        component: () => import("@/pages/SettingsInvitationsPage.vue"),
        meta: { requiresPermission: "user:write" },
      },
      {
        path: "settings/roles",
        name: "settings-roles",
        component: () => import("@/pages/SettingsRolesPage.vue"),
        meta: { requiresPermission: "role:read" },
      },
      {
        path: "settings/api-keys",
        name: "settings-api-keys",
        component: () => import("@/pages/SettingsAPIKeysPage.vue"),
        meta: { requiresPermission: "api_key:manage" },
      },
      {
        path: "inbox",
        name: "inbox",
        component: () => import("@/pages/InboxPage.vue"),
        meta: { requiresPermission: "conversation:read" },
      },
      {
        path: "settings/services",
        name: "settings-services",
        component: () => import("@/pages/SettingsServicesPage.vue"),
        meta: { requiresPermission: "service:read" },
      },
      {
        path: "settings/services/:id",
        name: "settings-service-detail",
        component: () => import("@/pages/SettingsServiceDetailPage.vue"),
        meta: { requiresPermission: "service:read" },
        props: true,
      },
      {
        path: "settings/bots",
        name: "settings-bots",
        component: () => import("@/pages/SettingsBotsPage.vue"),
        meta: { requiresPermission: "bot:read" },
      },
      {
        path: "settings/bots/:id",
        name: "settings-bot-detail",
        component: () => import("@/pages/SettingsBotDetailPage.vue"),
        meta: { requiresPermission: "bot:read" },
        props: true,
      },
      {
        path: "settings/tools",
        name: "settings-tools",
        component: () => import("@/pages/SettingsToolsPage.vue"),
        meta: { requiresPermission: "tool:read" },
      },
      {
        path: "settings/credentials",
        name: "settings-credentials",
        component: () => import("@/pages/SettingsCredentialsPage.vue"),
        meta: { requiresPermission: "credentials:read" },
      },
      {
        path: "settings/analytics",
        name: "settings-analytics",
        component: () => import("@/pages/SettingsAnalyticsPage.vue"),
        meta: { requiresPermission: "analytics:read" },
      },
      // /settings/channels was retired — channel management lives inside
      // the Service detail page now. Redirect any inbound link so old
      // bookmarks / docs land somewhere sensible.
      {
        path: "settings/channels",
        redirect: { name: "settings-services" },
      },
      {
        path: "aprendizado",
        name: "learning",
        component: () => import("@/pages/LearningCandidatesPage.vue"),
        meta: { requiresPermission: "learning:read" },
      },
      {
        path: "audit",
        name: "audit",
        component: () => import("@/pages/AuditPage.vue"),
        meta: { requiresPermission: "audit:view" },
      },
      {
        path: "forbidden",
        name: "forbidden",
        component: () => import("@/pages/ForbiddenPage.vue"),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/pages/NotFoundPage.vue"),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.bootstrapped) await auth.hydrate();
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  if (to.name === "login" && auth.isAuthenticated) {
    return { name: "dashboard" };
  }
  if (to.meta.requiresPermission && !auth.hasPermission(to.meta.requiresPermission)) {
    return { name: "forbidden" };
  }
  return true;
});
